import { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Calendar, Search, Stethoscope, X, CornerDownLeft } from 'lucide-react';
import { useSiteImage } from '@/contexts/SiteImagesContext';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

export default function EquipeMedica() {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpec, setFilterSpec] = useState('');
  const [specOpen, setSpecOpen] = useState(false);
  const [specIndex, setSpecIndex] = useState(-1);
  const specBoxRef = useRef(null);

  const canSeePrices = user && (user.mostra_prezzi || user.role === 'admin' || user.role === 'operator');
  const placeholderM = useSiteImage('placeholder_male');
  const siteSettings = useSiteSettings();
  const globalPrices = siteSettings.mostra_prezzi_globale === true;
  const globalAvailability = siteSettings.mostra_disponibilita_globale !== false;
  const bookingActive = siteSettings.prenotazioni_attive !== false;

  useEffect(() => {
    axios.get(`${API}/doctors`)
      .then(r => setDoctors(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const nameQ = searchTerm.trim().toLowerCase();
  const specQ = filterSpec.trim().toLowerCase();

  // Specialità presenti nello studio con numero di medici, ordinate per frequenza
  const specialties = useMemo(() => {
    const counts = {};
    doctors.forEach(d => { const k = (d.specializzazione || '').trim(); if (k) counts[k] = (counts[k] || 0) + 1; });
    return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'it'));
  }, [doctors]);

  const exactSpec = specialties.some(sp => sp.name.toLowerCase() === specQ);
  const specSuggestions = useMemo(() => {
    if (!specQ) return specialties.slice(0, 6);
    if (exactSpec) return [];
    return specialties.filter(sp => sp.name.toLowerCase().includes(specQ)).slice(0, 8);
  }, [specialties, specQ, exactSpec]);
  const topSpecialties = specialties.slice(0, 3);
  const showSpecPanel = specOpen && !exactSpec && (specSuggestions.length > 0 || specQ.length > 0);

  const pickSpec = (name) => { setFilterSpec(name); setSpecOpen(false); setSpecIndex(-1); };
  const onSpecKeyDown = (e) => {
    if (!showSpecPanel || specSuggestions.length === 0) { if (e.key === 'Escape') setSpecOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSpecIndex(i => (i + 1) % specSuggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSpecIndex(i => (i <= 0 ? specSuggestions.length - 1 : i - 1)); }
    else if (e.key === 'Enter') { if (specIndex >= 0) { e.preventDefault(); pickSpec(specSuggestions[specIndex].name); } else setSpecOpen(false); }
    else if (e.key === 'Escape') { setSpecOpen(false); setSpecIndex(-1); }
  };

  // Chiudi il pannello cliccando fuori
  useEffect(() => {
    const onDown = (e) => { if (specBoxRef.current && !specBoxRef.current.contains(e.target)) setSpecOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const highlight = (text) => {
    if (!specQ) return text;
    const i = text.toLowerCase().indexOf(specQ);
    if (i < 0) return text;
    return (<>{text.slice(0, i)}<mark className="bg-transparent text-[#238A92] font-semibold">{text.slice(i, i + specQ.length)}</mark>{text.slice(i + specQ.length)}</>);
  };
  const filteredDoctors = doctors.filter(d => {
    const matchSearch = !nameQ || `${d.nome} ${d.cognome}`.toLowerCase().includes(nameQ);
    const matchSpec = !specQ || (d.specializzazione || '').toLowerCase().includes(specQ);
    return matchSearch && matchSpec;
  });

  return (
    <div>
      <section className="hero-wash py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F2A37] mb-4" style={{fontFamily:'Space Grotesk'}}>La Nostra Equipe</h1>
          <p className="text-lg text-muted-foreground max-w-3xl">Professionisti qualificati e specialisti dedicati alla tua salute</p>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="relative w-full sm:max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input placeholder="Cerca per nome o cognome..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 pr-9" aria-label="Cerca per nome" data-testid="equipe-search-name" />
              {searchTerm && (
                <button type="button" onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37] transition-colors" aria-label="Pulisci nome" data-testid="equipe-search-name-clear"><X size={14} /></button>
              )}
            </div>
            <div className="relative w-full sm:max-w-sm" ref={specBoxRef}>
              <Stethoscope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Cerca per specialità (es. cardiologia)..."
                value={filterSpec}
                onChange={e => { setFilterSpec(e.target.value); setSpecOpen(true); setSpecIndex(-1); }}
                onFocus={() => setSpecOpen(true)}
                onKeyDown={onSpecKeyDown}
                className="pl-9 pr-9"
                aria-label="Cerca per specialità"
                aria-autocomplete="list"
                aria-expanded={showSpecPanel}
                aria-controls="equipe-spec-suggestions"
                autoComplete="off"
                data-testid="equipe-search-spec"
              />
              {filterSpec && (
                <button type="button" onClick={() => { setFilterSpec(''); setSpecIndex(-1); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37] transition-colors" aria-label="Pulisci specialità" data-testid="equipe-search-spec-clear"><X size={14} /></button>
              )}

              {showSpecPanel && (
                <div id="equipe-spec-suggestions" role="listbox" className="absolute left-0 right-0 top-full mt-1.5 z-30 rounded-lg border border-[hsl(var(--border))] bg-white/95 backdrop-blur-md shadow-[var(--shadow-md)] overflow-hidden" data-testid="equipe-spec-suggestions">
                  {specSuggestions.length > 0 ? (
                    <>
                      <p className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground">{specQ ? 'Specialità corrispondenti' : 'Specialità più richieste'}</p>
                      <ul className="max-h-72 overflow-y-auto pb-1">
                        {specSuggestions.map((sp, i) => (
                          <li key={sp.name} role="option" aria-selected={i === specIndex}>
                            <button
                              type="button"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => pickSpec(sp.name)}
                              onMouseEnter={() => setSpecIndex(i)}
                              className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-left transition-colors ${i === specIndex ? 'bg-[#238A92]/10 text-[#1F2A37]' : 'text-[#1F2A37] hover:bg-[hsl(var(--secondary))]'}`}
                              data-testid={`equipe-spec-suggestion-${i}`}
                            >
                              <span className="truncate">{highlight(sp.name)}</span>
                              <span className="flex items-center gap-2 flex-shrink-0">
                                <Badge variant="secondary" className="text-[11px] font-medium">{sp.count} {sp.count === 1 ? 'medico' : 'medici'}</Badge>
                                {i === specIndex && <CornerDownLeft size={12} className="text-muted-foreground" />}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <div className="px-3 py-3 text-sm" data-testid="equipe-spec-no-match">
                      <p className="text-muted-foreground mb-2">Nessuna specialità corrisponde a "{filterSpec.trim()}". Prova ad esempio:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {topSpecialties.map(sp => (
                          <button key={sp.name} type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickSpec(sp.name)} className="px-2.5 py-1 rounded-full text-xs border border-[#238A92]/40 text-[#238A92] hover:bg-[#238A92] hover:text-white transition-colors">{sp.name}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground self-center whitespace-nowrap" data-testid="equipe-count">{filteredDoctors.length} specialist{filteredDoctors.length !== 1 ? 'i' : 'a'}</p>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-80 rounded-xl" />)}
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className="text-center py-12" data-testid="equipe-empty">
              <p className="text-muted-foreground mb-3">Nessun dottore trovato{specQ ? ` per la specialità "${filterSpec.trim()}"` : ''}{nameQ ? ` con nome "${searchTerm.trim()}"` : ''}.</p>
              <Button variant="outline" size="sm" onClick={() => { setSearchTerm(''); setFilterSpec(''); }}>Azzera filtri</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDoctors.map(doc => (
                <Card key={doc.id} className="overflow-hidden hover:shadow-[var(--shadow-md)] transition-shadow duration-200">
                  <div className="aspect-square overflow-hidden bg-[hsl(var(--muted))]">
                    <img
                      src={doc.foto_url || placeholderM}
                      alt={`Dott. ${doc.cognome}`}
                      className="w-full h-full object-cover"
                      onError={e => { if (placeholderM) e.target.src = placeholderM; }}
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="text-lg font-bold text-[#1F2A37] mb-1" style={{fontFamily:'Space Grotesk'}}>Dott. {doc.nome} {doc.cognome}</h3>
                    <Badge className="mb-3 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]">{doc.specializzazione}</Badge>
                    {doc.bio && <p className="text-sm text-muted-foreground mb-3 leading-relaxed line-clamp-3">{doc.bio}</p>}
                    {/* Show tariffe if global AND doctor allows AND user can see prices */}
                    {globalPrices && doc.mostra_tariffe && canSeePrices && doc.tariffe && doc.tariffe.length > 0 && (
                      <div className="mb-3 space-y-1">
                        {doc.tariffe.slice(0, 3).map((t, i) => (
                          <div key={t.service_id || `t-${i}`} className="flex justify-between text-xs">
                            <span className="text-muted-foreground truncate mr-2">{t.nome_servizio}</span>
                            <span className="font-medium text-[#238A92] whitespace-nowrap">{Number(t.prezzo_effettivo ?? t.prezzo ?? 0).toFixed(2)} EUR</span>
                          </div>
                        ))}
                        {doc.tariffe.length > 3 && <p className="text-xs text-muted-foreground">+{doc.tariffe.length - 3} altri servizi</p>}
                      </div>
                    )}
                    {globalAvailability && doc.mostra_disponibilita !== false && doc.availability?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-[#1F2A37] mb-1">Disponibilità:</p>
                        <div className="flex flex-wrap gap-1">
                          {doc.availability.map((a) => (
                            <Badge key={`day-${a.day_of_week}`} variant="secondary" className="text-[10px]">
                              {DAYS[a.day_of_week]} {a.start_time}-{a.end_time}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <Link to={bookingActive ? `/prenota?doctor=${doc.id}` : "/contatti"} data-testid={`equipe-book-${doc.id}`}>
                      <Button size="sm" className="w-full bg-[#238A92] hover:bg-[#0F5E66] text-white">
                        <Calendar size={14} className="mr-1" /> {bookingActive ? `Prenota con Dott. ${doc.cognome}` : 'Contattaci'}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
